import React, { useState, useEffect } from 'react';
import PageLayout from 'components/common/PageLayout';
import DRESection from '../components/DRESection';
import BalanceteSection from '../components/BalanceteSection';
import ContasPagar from '../components/ContasPagar';
import ContasReceber from '../components/ContasReceber';
import ConciliacaoBancaria from '../components/ConciliacaoBancaria';
import HistoricoConciliacoes from '../components/HistoricoConciliacoes';
import ArvorePlanosConta from '../components/ArvorePlanosConta';
import Relatorios from '../components/Relatorios';
import { usePermissions } from 'hooks/usePermissions';
import { FileText, ChevronDown, Landmark, History, CreditCard, ArrowLeftRight, Wallet, BarChart3, ShieldCheck, UploadCloud, ScanLine, Layers3, X } from 'lucide-react';

const Administrativo = () => {
  const { hasPermission } = usePermissions();
  const podeEditarConciliacao = hasPermission('administrativo.edit');
  const [abaAtiva, setAbaAtiva] = useState('plano-conta');
  const [subAbaAtiva, setSubAbaAtiva] = useState('gerenciar');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMainSheet, setShowMainSheet] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Verificar se é mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Listener para navegação para Conciliação
  useEffect(() => {
    const handleNavigateToConciliacao = () => {
      setAbaAtiva('lancamentos');
      // Só navegar para Conciliação se tiver permissão, senão vai para Histórico
      if (podeEditarConciliacao) {
        setSubAbaAtiva('conciliacao');
      } else {
        setSubAbaAtiva('historico');
      }
    };

    window.addEventListener('navigate-to-conciliacao', handleNavigateToConciliacao);
    
    return () => {
      window.removeEventListener('navigate-to-conciliacao', handleNavigateToConciliacao);
    };
  }, [podeEditarConciliacao]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.dropdown-container')) {
        setShowDropdown(false);
      }
      if (showMainSheet && !event.target.closest('.main-sheet-container')) {
        setShowMainSheet(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown, showMainSheet]);

  // Sub-abas do Plano de Conta
  const subAbasPlanoConta = [
    {
      id: 'gerenciar',
      nome: 'Árvore de Planos de Conta',
      icon: FileText
    },
    {
      id: 'dre',
      nome: 'DRE',
      icon: FileText
    },
    {
      id: 'balancete',
      nome: 'Balancete',
      icon: FileText
    }
  ];

  // Sub-abas de Lançamentos (filtradas por permissão)
  const subAbasLancamentos = [
    {
      id: 'conciliacao',
      nome: 'Conciliação Bancária',
      icon: Landmark
    },
    {
      id: 'historico',
      nome: 'Histórico de Conciliações',
      icon: History
    }
  ].filter(aba => {
    // Mostrar "Conciliação Bancária" apenas se tiver permissão de editar
    if (aba.id === 'conciliacao') {
      return podeEditarConciliacao;
    }
    return true; // Sempre mostrar Histórico
  });

  const renderizarConteudoPlanoConta = () => {
    switch (subAbaAtiva) {
      case 'dre':
        return <DRESection />;
      case 'balancete':
        return <BalanceteSection />;
      case 'gerenciar':
      default:
        return <ArvorePlanosConta />;
    }
  };

  const renderizarConteudoLancamentos = () => {
    switch (subAbaAtiva) {
      case 'conciliacao':
        // Só renderizar se tiver permissão
        return podeEditarConciliacao ? <ConciliacaoBancaria /> : <HistoricoConciliacoes />;
      case 'historico':
        return <HistoricoConciliacoes />;
      default:
        // Se não tiver permissão, mostrar Histórico por padrão
        return podeEditarConciliacao ? <ConciliacaoBancaria /> : <HistoricoConciliacoes />;
    }
  };

  const mainTabs = [
    { id: 'plano-conta', label: 'Plano de Conta', icon: FileText },
    { id: 'contas-pagar', label: 'Contas a Pagar', icon: CreditCard },
    { id: 'lancamentos', label: 'Lançamentos', icon: ArrowLeftRight },
    { id: 'contas-receber', label: 'Contas a Receber', icon: Wallet },
    { id: 'relatorios', label: 'Relatórios', icon: BarChart3 },
    { id: 'auditoria', label: 'Auditoria', icon: ShieldCheck, inDevelopment: true },
    { id: 'importacoes', label: 'Importações', icon: UploadCloud, inDevelopment: true },
    { id: 'centro-captura', label: 'Centro de Captura', icon: ScanLine, inDevelopment: true }
  ];

  const handleMainTabSelect = (tabId) => {
    setAbaAtiva(tabId);
    if (tabId === 'plano-conta') {
      setSubAbaAtiva('gerenciar');
    } else if (tabId === 'lancamentos') {
      setSubAbaAtiva('conciliacao');
    }
    setShowMainSheet(false);
  };

  const mainTabAtivo = mainTabs.find(tab => tab.id === abaAtiva);
  const MainTabIcon = mainTabAtivo?.icon || BarChart3;
  const desktopSidebarColumnClassName = 'w-64 shrink-0 self-start';
  const desktopSidebarShellClassName = 'sticky top-[calc(var(--header-shell-height)-16px)] self-start w-full bg-[color:var(--surface)]/96 border border-t-0 border-l-0 border-[color:var(--header-control-border-subtle)] rounded-r-[24px] rounded-l-none rounded-tr-none px-3 pt-2.5 pb-4 shadow-[0_14px_36px_rgba(2,6,23,0.16)]';
  const desktopSidebarHeaderClassName = 'mb-3 border-b border-[color:var(--header-control-border-subtle)] px-2 pb-2';
  const desktopSidebarListClassName = 'app-scroll-subtle flex max-h-[calc(100dvh-168px)] flex-col gap-1.5 overflow-y-auto pr-1';
  const desktopSidebarItemClassName = 'flex min-h-[46px] items-center gap-3 rounded-[14px] border px-3 py-3 text-sm transition-[background-color,color,border-color,box-shadow] duration-200';
  const desktopSidebarLabelClassName = 'min-w-0 flex-1 truncate text-left';
  const desktopSidebarInactiveClassName = 'border-transparent text-[color:var(--text-primary)]/82 hover:bg-[color:var(--surface-muted)]/38 hover:text-[color:var(--text-primary)] hover:border-[color:var(--header-control-border-subtle)]';
  const desktopSidebarActiveClassName = 'bg-[color:var(--surface-muted)]/58 text-[color:var(--text-primary)] border-[color:var(--header-control-border-subtle)] shadow-[inset_0_1px_0_rgba(255,255,255,0.03),0_0_0_1px_rgba(168,85,247,0.14)]';
  const desktopSidebarIconInactiveClassName = 'text-[color:var(--text-muted)] opacity-70';
  const desktopSidebarIconActiveClassName = 'text-[color:var(--accent)] opacity-95';
  const desktopSubtabsShellClassName = 'app-scroll-subtle overflow-x-auto rounded-[18px] border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)]/88 px-2 py-2 shadow-[0_10px_30px_rgba(2,6,23,0.12)]';
  const desktopSubtabsTrackClassName = 'flex min-w-full w-max items-center gap-1.5';
  const desktopSubtabButtonBaseClassName = 'group relative flex min-h-[44px] shrink-0 items-center gap-2.5 rounded-[14px] border px-4 py-2.5 text-sm transition-[background-color,color,border-color,box-shadow,transform] duration-200 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]/40 focus:ring-offset-0';
  const desktopSubtabActiveClassName = 'border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/55 text-[color:var(--text-primary)] shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_0_0_1px_rgba(168,85,247,0.12)]';
  const desktopSubtabInactiveClassName = 'border-transparent text-[color:var(--text-muted)] hover:border-[color:var(--header-control-border-subtle)] hover:bg-[color:var(--surface-muted)]/38 hover:text-[color:var(--text-primary)]';
  const mobileSubtabsShellClassName = 'bg-[color:var(--surface)]/92 rounded-[18px] border border-[color:var(--header-control-border-subtle)] px-3 py-2 shadow-[0_10px_24px_rgba(2,6,23,0.12)]';
  const mobileSubtabTriggerClassName = 'w-full flex min-h-[48px] items-center justify-between gap-3 rounded-[14px] border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)] px-4 py-3 text-left text-sm text-[color:var(--text-primary)] transition-[background-color,border-color,box-shadow] duration-200 hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]/40 focus:ring-offset-0';
  const mobileSubtabListClassName = 'absolute z-[80] mt-2 w-full overflow-hidden rounded-[16px] border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)] shadow-[0_18px_40px_rgba(2,6,23,0.24)]';
  const mobileSubtabItemBaseClassName = 'w-full flex min-h-[48px] items-center gap-3 px-4 py-3 text-left text-sm transition-[background-color,color] duration-150';

  const renderDesktopSidebar = () => (
    <div className={desktopSidebarColumnClassName}>
      <aside className={desktopSidebarShellClassName}>
        <div className={desktopSidebarHeaderClassName}>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--text-muted)]/55">
            Administrativo
          </p>
          <p className="mt-1 text-[13px] text-[color:var(--text-muted)]/82">
            Navegação da diretoria
          </p>
        </div>
        <div className={desktopSidebarListClassName}>
          {mainTabs.map((tab) => {
            const IconComponent = tab.icon;
            const isActive = abaAtiva === tab.id;
            const isInDevelopment = Boolean(tab.inDevelopment);
            return (
              <button
                key={tab.id}
                onClick={() => handleMainTabSelect(tab.id)}
                title={isInDevelopment ? `${tab.label} (Em desenvolvimento)` : tab.label}
                className={`${desktopSidebarItemClassName} ${
                  isActive
                    ? desktopSidebarActiveClassName
                    : desktopSidebarInactiveClassName
                }`}
              >
                <IconComponent className={`h-4 w-4 flex-shrink-0 transition-colors duration-200 ${
                  isActive ? desktopSidebarIconActiveClassName : desktopSidebarIconInactiveClassName
                }`} />
                <span className={`${desktopSidebarLabelClassName} ${isActive ? 'font-semibold text-[color:var(--text-primary)]' : 'font-medium'}`}>
                  {tab.label}
                </span>
                {isInDevelopment && (
                  <span className="inline-flex items-center whitespace-nowrap rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[color:var(--status-warn)]">
                    Em breve
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </aside>
    </div>
  );

  const renderDesktopSubtabs = (subAbas) => (
    <div className={desktopSubtabsShellClassName}>
      <div className={desktopSubtabsTrackClassName}>
        {subAbas.map((aba) => {
          const IconComponent = aba.icon;
          const isActive = subAbaAtiva === aba.id;
          return (
            <button
              key={aba.id}
              onClick={() => setSubAbaAtiva(aba.id)}
              className={`${desktopSubtabButtonBaseClassName} ${
                isActive
                  ? desktopSubtabActiveClassName
                  : desktopSubtabInactiveClassName
              }`}
            >
              <IconComponent className={`h-4 w-4 flex-shrink-0 transition-colors duration-200 ${
                isActive ? 'text-[color:var(--accent)] opacity-95' : 'text-[color:var(--text-muted)] opacity-65 group-hover:opacity-85'
              }`} />
              <span className={`whitespace-nowrap ${isActive ? 'font-semibold text-[color:var(--text-primary)]' : 'font-medium'}`}>
                {aba.nome}
              </span>
              <span
                aria-hidden="true"
                className={`absolute inset-x-3 bottom-1 h-0.5 rounded-full transition-opacity duration-200 ${
                  isActive ? 'bg-[color:var(--accent)] opacity-100' : 'opacity-0'
                }`}
              />
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderMobileSubtabs = (subAbas) => (
    <div className={mobileSubtabsShellClassName}>
      <div className="dropdown-container relative">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className={mobileSubtabTriggerClassName}
        >
          <span className="min-w-0 flex items-center gap-3">
            {(() => {
              const activeSubAba = subAbas.find(aba => aba.id === subAbaAtiva);
              const ActiveIcon = activeSubAba?.icon || FileText;
              return <ActiveIcon className="h-4 w-4 flex-shrink-0 text-[color:var(--accent)] opacity-90" />;
            })()}
            <span className="truncate font-medium">
              {subAbas.find(aba => aba.id === subAbaAtiva)?.nome || 'Selecionar sub-aba'}
            </span>
          </span>
          <ChevronDown className={`h-4 w-4 flex-shrink-0 text-[color:var(--text-muted)] transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} />
        </button>
        
        {showDropdown && (
          <div className={mobileSubtabListClassName}>
            {subAbas.map((aba) => {
              const IconComponent = aba.icon;
              const isActive = subAbaAtiva === aba.id;
              return (
                <button
                  key={aba.id}
                  onClick={() => {
                    setSubAbaAtiva(aba.id);
                    setShowDropdown(false);
                  }}
                  className={`${mobileSubtabItemBaseClassName} ${
                    isActive
                      ? 'bg-[color:var(--surface-muted)]/90 text-[color:var(--text-primary)]'
                      : 'text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]/70'
                  }`}
                >
                  <IconComponent className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-[color:var(--accent)] opacity-95' : 'text-[color:var(--text-muted)] opacity-75'}`} />
                  <span className={`min-w-0 flex-1 truncate ${isActive ? 'font-semibold' : 'font-medium'}`}>
                    {aba.nome}
                  </span>
                  {isActive && (
                    <span className="h-2 w-2 flex-shrink-0 rounded-full bg-[color:var(--accent)]" aria-hidden="true" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <PageLayout>
      <div className="w-full px-4 sm:px-6 md:px-0 md:pr-6 lg:pr-8 pt-4 sm:pt-5 md:pt-0 pb-4 sm:pb-5 overflow-x-hidden">

        {/* Seletor de Abas */}
        {isMobile && (
          <div className="mb-6 main-sheet-container">
            <div className="bg-[color:var(--surface)] border border-[color:var(--header-control-border-subtle)] p-2 rounded-2xl shadow-[0_10px_24px_rgba(2,6,23,0.12)]">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowMainSheet(true)}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/60 px-3 text-sm font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]/40"
                >
                  <Layers3 className="h-4 w-4 text-[color:var(--accent)]" />
                  Módulos
                </button>

                <div className="min-w-0 flex-1 rounded-xl border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)] px-3 py-2.5">
                  <div className="flex min-h-[24px] items-center gap-2">
                    <MainTabIcon className="h-4 w-4 flex-shrink-0 text-[color:var(--accent)]" />
                    <span className="truncate text-sm font-semibold text-[color:var(--text-primary)]">
                      {mainTabAtivo?.label || 'Selecionar módulo'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {showMainSheet && (
              <div className="fixed inset-0 z-[100] flex items-end justify-center">
                <div
                  className="absolute inset-0 bg-black/60"
                  onClick={() => setShowMainSheet(false)}
                  aria-hidden="true"
                />
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-label="Selecionar módulo administrativo"
                  className="relative w-full max-w-md rounded-t-2xl border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)] p-4 shadow-[0_-12px_40px_rgba(2,6,23,0.35)]"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-sm font-semibold text-[color:var(--text-primary)]">Navegação da diretoria</p>
                    <button
                      type="button"
                      onClick={() => setShowMainSheet(false)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[color:var(--header-control-border-subtle)] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]/40"
                      aria-label="Fechar menu de módulos"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="app-scroll-subtle max-h-[60dvh] space-y-1 overflow-y-auto pr-1">
                    {mainTabs.map((tab) => {
                      const IconComponent = tab.icon;
                      const isActive = abaAtiva === tab.id;
                      return (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => handleMainTabSelect(tab.id)}
                          className={`w-full flex min-h-[48px] items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors ${
                            isActive
                              ? 'border-[color:var(--accent)] bg-[color:var(--surface-muted)]/80 text-[color:var(--text-primary)]'
                              : 'border-transparent text-[color:var(--text-primary)] hover:border-[color:var(--header-control-border-subtle)] hover:bg-[color:var(--surface-muted)]/70'
                          }`}
                        >
                          <IconComponent className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-[color:var(--accent)]' : 'text-[color:var(--text-muted)]'}`} />
                          <span className={`min-w-0 flex-1 truncate ${isActive ? 'font-semibold' : 'font-medium'}`}>
                            {tab.label}
                          </span>
                          {tab.inDevelopment && (
                            <span className="inline-flex items-center whitespace-nowrap rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[color:var(--status-warn)]">
                              Em breve
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sub-abas do Plano de Conta */}
        {isMobile && abaAtiva === 'plano-conta' && (
          <div className="mb-6">
            {renderMobileSubtabs(subAbasPlanoConta)}
          </div>
        )}

        {/* Conteúdo baseado na aba ativa */}
        {!isMobile ? (
          abaAtiva === 'plano-conta' ? (
            <div className="flex items-start gap-6">
              {renderDesktopSidebar()}
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="min-w-0">
                  {renderDesktopSubtabs(subAbasPlanoConta)}
                </div>
                <div className="min-w-0">
                  {renderizarConteudoPlanoConta()}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex gap-6">
              {renderDesktopSidebar()}
              <div className="flex-1 min-w-0">
                {/* Os seletores de subaba desktop/mobile ficam fora deste refinamento para evitar regressão no layout. */}
                {abaAtiva === 'contas-pagar' ? (
                <ContasPagar />
              ) : abaAtiva === 'lancamentos' ? (
                <div className="pt-3">
                  <div className="mb-3">
                    <div className="relative">
                      {isMobile ? (
                        renderMobileSubtabs(subAbasLancamentos)
                      ) : (
                        renderDesktopSubtabs(subAbasLancamentos)
                      )}
                    </div>
                  </div>
                  {renderizarConteudoLancamentos()}
                </div>
              ) : abaAtiva === 'contas-receber' ? (
                <ContasReceber />
              ) : abaAtiva === 'relatorios' ? (
                <Relatorios />
              ) : (
                <div className="bg-[color:var(--surface)] p-8 rounded-lg shadow-sm border">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-[color:var(--surface-muted)] rounded-full flex items-center justify-center mx-auto mb-4">
                      <MainTabIcon className="h-7 w-7 text-[color:var(--text-muted)]" />
                    </div>
                    <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-2">
                      {abaAtiva === 'auditoria' && 'Auditoria'}
                      {abaAtiva === 'importacoes' && 'Importações'}
                      {abaAtiva === 'centro-captura' && 'Centro de Captura'}
                    </h3>
                    <p className="text-[color:var(--text-muted)] mb-6">
                      Esta funcionalidade está em desenvolvimento e estará disponível em breve.
                    </p>
                    <div className="bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-lg p-4 max-w-md mx-auto">
                      <p className="text-[color:var(--text-primary)] text-sm">
                        <strong>Em breve:</strong> Esta Seção terá todas as funcionalidades necessárias para
                        {abaAtiva === 'auditoria' && ' visualizar logs de auditoria.'}
                        {abaAtiva === 'importacoes' && ' realizar Importações de dados.'}
                        {abaAtiva === 'centro-captura' && ' capturar documentos e informações.'}
                      </p>
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>
          )
        ) : (
          <div className="rounded-b-lg p-0">
            {abaAtiva === 'plano-conta' ? (
              renderizarConteudoPlanoConta()
            ) : abaAtiva === 'contas-pagar' ? (
              <ContasPagar />
            ) : abaAtiva === 'lancamentos' ? (
              <div>
                  <div className="mb-3">
                    <div className="relative">
                      {isMobile ? (
                        renderMobileSubtabs(subAbasLancamentos)
                      ) : (
                        renderDesktopSubtabs(subAbasLancamentos)
                      )}
                    </div>
                  </div>
                  {renderizarConteudoLancamentos()}
                </div>
            ) : abaAtiva === 'contas-receber' ? (
              <ContasReceber />
            ) : abaAtiva === 'relatorios' ? (
              <Relatorios />
            ) : (
              <div className="bg-[color:var(--surface)] p-8 rounded-lg shadow-sm border">
                <div className="text-center">
                  <div className="w-16 h-16 bg-[color:var(--surface-muted)] rounded-full flex items-center justify-center mx-auto mb-4">
                    <MainTabIcon className="h-7 w-7 text-[color:var(--text-muted)]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-2">
                    {abaAtiva === 'auditoria' && 'Auditoria'}
                    {abaAtiva === 'importacoes' && 'Importações'}
                    {abaAtiva === 'centro-captura' && 'Centro de Captura'}
                  </h3>
                  <p className="text-[color:var(--text-muted)] mb-6">
                    Esta funcionalidade está em desenvolvimento e estará disponível em breve.
                  </p>
                  <div className="bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-lg p-4 max-w-md mx-auto">
                    <p className="text-[color:var(--text-primary)] text-sm">
                      <strong>Em breve:</strong> Esta Seção terá todas as funcionalidades necessárias para
                      {abaAtiva === 'auditoria' && ' visualizar logs de auditoria.'}
                      {abaAtiva === 'importacoes' && ' realizar Importações de dados.'}
                      {abaAtiva === 'centro-captura' && ' capturar documentos e informações.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default Administrativo; 
